// src/stores/authStore.ts
import { create } from "zustand";
import { persist } from "zustand/middleware";
import {
  User,
  signInAnonymously,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  sendPasswordResetEmail,
  sendEmailVerification,
  updateProfile,
  linkWithCredential,
  signInWithCredential,
  EmailAuthProvider,
  deleteUser,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
  linkWithPopup,
  fetchSignInMethodsForEmail,
  AuthCredential,
} from "firebase/auth";
import {
  doc,
  setDoc,
  getDoc,
  deleteDoc,
  updateDoc,
  onSnapshot,
  Timestamp,
  serverTimestamp,
} from "firebase/firestore";
import { auth, db } from "../services/firebase";
import { CompleteUserProfile, DEFAULT_USER_PROFILE } from "../types/profile";

export type AuthMode = "anonymous" | "registered";

export interface GDPRConsent {
  necessary: boolean;
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
  /** When the consent was last updated. For the placeholder
   *  "system default" record this is set to the Unix epoch
   *  (1970-01-01) so it's distinguishable from a real consent
   *  timestamp; combined with `consent_recorded` it tells you
   *  whether the user has actually confirmed anything yet. */
  consentDate: Date;
  privacyPolicyVersion: string;
  /** True ONLY when the user has explicitly confirmed their
   *  consent choices via the cookie banner / privacy settings.
   *  False on the placeholder default record we write at signup
   *  to satisfy the Firestore-rules schema. The cookie banner
   *  flips this to true on accept-or-decline (either is an
   *  active choice); analytics gating reads `analytics` only
   *  when `consent_recorded` is true. */
  consent_recorded?: boolean;
  ageVerified?: boolean;
  ageVerificationDate?: Date;
}

export interface UserProfile {
  uid: string;
  email?: string;
  displayName?: string;
  emailVerified: boolean;
  userType: AuthMode;
  createdAt: Date;
  lastActive: Date;
  preferences: {
    locationPrivacy: "coarse" | "precise";
    theme: "light" | "dark" | "system";
    language: string;
  };
  gdprConsent: GDPRConsent;
  travelProfile?: CompleteUserProfile;
  /** Override that bypasses the desktop/tablet gate (DesktopGate.tsx).
   *  Intentionally NOT exposed in any UI — set manually in the
   *  Firestore console for staff / QA who need desktop access. The
   *  gate treats anything other than `true` (missing, false,
   *  undefined) as "no override → gate desktop visitors". */
  allowDesktop?: boolean;
}

interface PendingGoogleConsent {
  /** Firebase UID of the user awaiting consent. We hold this so we
   *  can delete the user if they decline, since the auth user already
   *  exists at this point. */
  uid: string;
  /** Email from Google — shown on the consent screen so they know
   *  which account they're agreeing as. */
  email: string | null;
  /** Display name from Google — used for the profile if they accept. */
  displayName: string | null;
}

/** Set when a user tried to sign in with Google but the email is
 *  already registered with email/password. We capture the OAuth
 *  credential and ask them to enter their existing password to link
 *  the two providers automatically. After link, future Google
 *  sign-ins on the same email "just work". */
interface PendingGoogleLink {
  /** Email associated with both the Google account and the existing
   *  email/password account — the user will sign in to that. */
  email: string;
  /** Pre-existing Google OAuth credential captured from the failed
   *  popup attempt. We replay it as `linkWithCredential` after the
   *  user successfully signs in with their password, attaching
   *  Google as a second provider on the same Firebase user. */
  credential: AuthCredential;
  /** Display name from the Google account, useful for the prompt. */
  displayName: string | null;
}

interface AuthState {
  // State
  user: User | null;
  profile: UserProfile | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  authMode: AuthMode;
  error: string | null;
  /** Set when a Google sign-in succeeded but the user hasn't given
   *  GDPR consent yet. While this is non-null, the user is signed
   *  into Firebase but NOT marked as isAuthenticated — the auth
   *  modal renders a consent step instead of closing. */
  pendingGoogleConsent: PendingGoogleConsent | null;

  /** Set when a Google sign-in attempt revealed that the email is
   *  already registered with email/password. The auth modal renders
   *  a "sign in with your password to link Google" step. */
  pendingGoogleLink: PendingGoogleLink | null;

  /** Single source of truth for the global AuthModal's visibility +
   *  initial tab. Replaces per-component show-modal state in
   *  Sidebar / ConversationDrawerContent / UserMenu. Centralizing
   *  this is critical: when the Google popup steals focus and any
   *  containing menu/drawer unmounts, the modal stays alive because
   *  it's mounted at App level and reads its open state from the
   *  store. */
  authModal: {
    open: boolean;
    initialTab: "login" | "signup";
  };

  // Actions
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signUpWithEmail: (
    email: string,
    password: string,
    displayName?: string,
    gdprConsent?: Partial<GDPRConsent>
  ) => Promise<void>;
  signOut: () => Promise<void>;
  upgradeAnonymousAccount: (
    email: string,
    password: string,
    displayName?: string,
    gdprConsent?: Partial<GDPRConsent>
  ) => Promise<void>;
  signInWithGoogle: (
    gdprConsent?: Partial<GDPRConsent>,
    displayName?: string
  ) => Promise<void>;
  /** Complete a pending Google sign-in by recording consent and
   *  writing the profile. Called from the consent step in the auth
   *  modal. */
  completeGoogleConsent: (
    gdprConsent: Partial<GDPRConsent>
  ) => Promise<void>;
  /** Cancel a pending Google sign-in. Deletes the Firebase user
   *  (since they declined consent) and clears local state. */
  cancelGoogleConsent: () => Promise<void>;
  /** Finish the auto-reconcile flow: after the user provided their
   *  password for the existing email account, sign in and link the
   *  captured Google credential to that user. */
  completeGoogleLink: (password: string) => Promise<void>;
  /** Drop the pending Google link without signing in. The user can
   *  return to the regular tabs. */
  cancelGoogleLink: () => void;
  /** Open the global auth modal at a specific tab. Replaces the
   *  per-component state that used to live in each call site. */
  openAuthModal: (initialTab?: "login" | "signup") => void;
  /** Close the global auth modal. */
  closeAuthModal: () => void;
  resetPassword: (email: string) => Promise<void>;
  resendVerification: () => Promise<void>;
  deleteAccount: () => Promise<void>;
  updateUserProfile: (updates: Partial<UserProfile>) => Promise<void>;
  updateGDPRConsent: (consent: Partial<GDPRConsent>) => Promise<void>;
  /** Persist explicit user consent (cookie banner accept/decline,
   *  privacy settings update). Always flips `consent_recorded` to
   *  true and stamps `consentDate` to the current moment, so the
   *  consent state is unambiguous. Safe to call before the user
   *  doc exists — writes via setDoc-merge so a missing profile
   *  doesn't fail. Compared to updateGDPRConsent (which writes
   *  the entire profile back via setDoc), this method only
   *  touches gdprConsent + lastActive — friendly to the
   *  Firestore rule that gates allowed-key diffs. */
  recordGdprConsent: (consent: {
    necessary?: boolean;
    functional?: boolean;
    analytics?: boolean;
    marketing?: boolean;
  }) => Promise<void>;
  initializeAuth: () => Promise<void>;
  clearError: () => void;
}

const DEFAULT_PREFERENCES = {
  locationPrivacy: "coarse" as const,
  theme: "system" as const,
  language: "en",
};

// Placeholder default written to Firestore at first auth so the
// users/{uid} doc satisfies the Firestore-rules schema (which
// requires gdprConsent.necessary, consentDate, privacyPolicyVersion).
//
// `consent_recorded: false` is the truthy signal — it means
// "this is the system-stamped default; the user hasn't actually
// agreed to anything yet". The cookie banner flips this to true
// on accept-or-decline.
//
// `consentDate` uses the Unix epoch as a sentinel. It satisfies
// the `is timestamp` rule check while being obviously not a
// real consent timestamp. Once the user records consent, this
// flips to the actual moment they clicked.
const PLACEHOLDER_CONSENT_DATE = new Date(0);

const DEFAULT_GDPR_CONSENT: GDPRConsent = {
  necessary: true,
  functional: false,
  analytics: false,
  marketing: false,
  consentDate: PLACEHOLDER_CONSENT_DATE,
  privacyPolicyVersion: "3.0",
  consent_recorded: false,
};

// ──────────────────────────────────────────────────────────────────
// Race condition guard: when an explicit signup flow (email signup,
// Google signin/signup, anonymous upgrade) is in progress, the auth
// state listener will fire BEFORE the explicit method has had a chance
// to write the profile with proper GDPR consent. Without a guard, the
// listener writes a default profile first, then the explicit method's
// write either races or is rejected by Firestore rules (which only
// allow specific key diffs on `update`).
//
// This module-level flag is set at the start of any explicit profile-
// creating method and cleared once that method has finished writing
// its profile. The listener checks it and skips its own profile work
// during the window.
//
// It's deliberately module-scope rather than zustand state so that
// (a) it doesn't get persisted to localStorage and stuck, and (b) a
// page refresh clears it cleanly.
// ──────────────────────────────────────────────────────────────────
let SIGNUP_IN_PROGRESS = false;

const beginSignup = () => {
  SIGNUP_IN_PROGRESS = true;
};
const endSignup = () => {
  SIGNUP_IN_PROGRESS = false;
};

// ──────────────────────────────────────────────────────────────────
// Listener handles. Stored at module scope so initializeAuth can
// clean up previous installations before installing new ones (the
// previous code returned the unsubscribe from inside a Promise
// executor — it went nowhere, leaving listeners alive forever).
//
// `currentAuthUnsub` — the onAuthStateChanged subscription.
//   Reset on each initializeAuth call.
// `currentProfileUnsub` — the live profile onSnapshot
//   subscription. Reset on each auth-state-change so a new uid
//   gets its own listener and old ones don't leak.
// ──────────────────────────────────────────────────────────────────
let currentAuthUnsub: (() => void) | null = null;
let currentProfileUnsub: (() => void) | null = null;
let listenedProfileUid: string | null = null;

// Helper function to remove undefined values from objects before saving to Firestore
const cleanFirestoreData = (obj: any): any => {
  const cleaned: any = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined && value !== null) {
      if (typeof value === "object" && value instanceof Date) {
        cleaned[key] = value;
      } else if (typeof value === "object" && !Array.isArray(value)) {
        cleaned[key] = cleanFirestoreData(value);
      } else {
        cleaned[key] = value;
      }
    }
  }
  return cleaned;
};

/** Translate a Firebase auth error into a user-readable string.
 *  Returns null when the error code is one we treat as a non-error
 *  (popup dismissed, etc.) — caller should clear error state and bail
 *  silently in that case. */
function friendlyAuthError(err: any): string | null {
  const code = err?.code || "";
  switch (code) {
    // ── Non-errors: user cancelled the popup ──
    case "auth/popup-closed-by-user":
    case "auth/cancelled-popup-request":
    case "auth/user-cancelled":
      return null;

    // ── Sign-in failures ──
    case "auth/invalid-credential":
    case "auth/wrong-password":
    case "auth/user-not-found":
      return "Email or password is incorrect.";
    case "auth/invalid-email":
      return "That email address doesn't look right. Check the format.";
    case "auth/user-disabled":
      return "This account has been disabled. Contact support.";
    case "auth/too-many-requests":
      return "Too many failed attempts. Wait a few minutes and try again, or reset your password.";

    // ── Sign-up failures ──
    case "auth/email-already-in-use":
      return "An account with this email already exists. Try signing in instead.";
    case "auth/weak-password":
      return "Password is too weak. Use at least 8 characters with a mix of letters and numbers.";
    case "auth/operation-not-allowed":
      return "This sign-in method isn't enabled. Contact support.";

    // ── Google / popup-specific ──
    case "auth/popup-blocked":
      return "Your browser blocked the sign-in popup. Allow popups for this site and try again.";
    case "auth/credential-already-in-use":
    case "auth/account-exists-with-different-credential":
      return "This Google account is already linked to a different Instant Tours account. Sign in with that account directly.";

    // ── Linking failures ──
    case "auth/provider-already-linked":
      return "Your account is already linked to this provider.";
    case "auth/requires-recent-login":
      return "For security, sign out and sign back in before making this change.";

    // ── Network / domain ──
    case "auth/network-request-failed":
      return "Network error. Check your connection and try again.";
    case "auth/unauthorized-domain":
      return "This domain isn't authorized for sign-in. Contact support.";
    case "auth/internal-error":
      return "Something went wrong on our end. Try again in a moment.";

    default:
      // Unknown code: surface the raw message but trimmed of the
      // "Firebase: " prefix so it reads more naturally.
      const raw =
        err?.message?.replace(/^Firebase:\s*/, "").replace(/\s*\([^)]*\)\s*$/, "") ||
        "Sign-in failed.";
      return raw;
  }
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      profile: null,
      isAuthenticated: false,
      isLoading: true,
      authMode: "anonymous",
      error: null,
      pendingGoogleConsent: null,
      pendingGoogleLink: null,
      authModal: { open: false, initialTab: "login" },

      // Initialize authentication listener
      initializeAuth: async () => {
        // Clean up any previous installation. In production this
        // matters less (zustand action identity is stable, the
        // App.tsx effect deps don't change, so it fires once).
        // In React StrictMode dev, effects fire twice and we end
        // up with duplicate listeners; without this cleanup, every
        // auth state change runs the handler twice.
        if (currentAuthUnsub) {
          try {
            currentAuthUnsub();
          } catch {
            /* ignore */
          }
          currentAuthUnsub = null;
        }
        if (currentProfileUnsub) {
          try {
            currentProfileUnsub();
          } catch {
            /* ignore */
          }
          currentProfileUnsub = null;
          listenedProfileUid = null;
        }

        return new Promise(async (resolve) => {
          let hasResolved = false;

          console.log("Initializing auth state listener...");

          const unsubscribe = onAuthStateChanged(auth, async (user) => {
            console.log(
              "Auth state changed:",
              user
                ? `User: ${user.uid} (isAnonymous: ${user.isAnonymous})`
                : "No user"
            );

            try {
              if (user) {
                // Load user profile
                const profileDoc = await getDoc(doc(db, "users", user.uid));
                let profile: UserProfile;

                if (profileDoc.exists()) {
                  const data = profileDoc.data();
                  profile = {
                    uid: user.uid,
                    emailVerified: user.emailVerified,
                    userType:
                      data.userType ||
                      (user.isAnonymous ? "anonymous" : "registered"),
                    createdAt: data.createdAt?.toDate() || new Date(),
                    lastActive: data.lastActive?.toDate() || new Date(),
                    preferences: data.preferences || DEFAULT_PREFERENCES,
                    gdprConsent: {
                      ...DEFAULT_GDPR_CONSENT,
                      ...data.gdprConsent,
                      consentDate:
                        data.gdprConsent?.consentDate?.toDate() || new Date(),
                      ageVerificationDate:
                        data.gdprConsent?.ageVerificationDate?.toDate() ||
                        undefined,
                    },
                    travelProfile: data.travelProfile
                      ? {
                          ...DEFAULT_USER_PROFILE,
                          ...data.travelProfile,
                          lastUpdated:
                            data.travelProfile.lastUpdated?.toDate() ||
                            new Date(),
                          completedAt:
                            data.travelProfile.completedAt?.toDate() || null,
                        }
                      : undefined,
                  };

                  // Only add optional fields if they exist and are not empty
                  if (
                    data.email &&
                    typeof data.email === "string" &&
                    data.email.trim()
                  ) {
                    profile.email = data.email;
                  }
                  if (
                    data.displayName &&
                    typeof data.displayName === "string" &&
                    data.displayName.trim()
                  ) {
                    profile.displayName = data.displayName;
                  }
                  // Desktop gate override. Strict equality — only the
                  // boolean `true` bypasses; any truthy-string-by-accident
                  // (e.g. "true" pasted from a console) will still gate.
                  // Field is set manually in the Firestore console; see
                  // DesktopGate.tsx for the consumer.
                  if (data.allowDesktop === true) {
                    profile.allowDesktop = true;
                  }

                  console.log(
                    "Loaded existing profile for returning user:",
                    profile.userType
                  );
                } else {
                  // No existing profile in Firestore. Two cases:
                  // 1. Truly new user (anonymous sign-in, or rare race
                  //    where the explicit signup path lost network)
                  // 2. An explicit signup is mid-flight — the
                  //    signup method will write the profile imminently
                  //    with the user's actual GDPR consent.
                  //
                  // For case 2, we MUST NOT pre-empt with default
                  // values, or we either lose the user's consent
                  // selections (marketing pref, age verified date) or
                  // the explicit method's `setDoc` gets rejected by
                  // Firestore rules (which forbid arbitrary updates).
                  //
                  // Special case 3: Google sign-in deferred consent.
                  // The user signed in with Google but hasn't agreed
                  // to terms yet. We MUST NOT mark them as
                  // isAuthenticated — the consent step in the auth
                  // modal needs to gate access. We also must NOT
                  // write a profile, because consent isn't recorded
                  // yet.
                  const pendingConsent = get().pendingGoogleConsent;
                  if (pendingConsent && pendingConsent.uid === user.uid) {
                    console.log(
                      "Auth listener: Google sign-in awaiting consent, holding state."
                    );
                    set({
                      user,
                      profile: null,
                      isAuthenticated: false,
                      authMode: "anonymous",
                      isLoading: false,
                      error: null,
                    });
                    if (!hasResolved) {
                      hasResolved = true;
                      resolve();
                    }
                    return;
                  }

                  if (SIGNUP_IN_PROGRESS) {
                    console.log(
                      "Auth listener: signup in progress, deferring profile creation to explicit method."
                    );
                    // We still need SOMETHING in store state so the rest
                    // of the app knows the user is authenticated. Use a
                    // skeleton profile that will be replaced when the
                    // explicit method completes its Firestore write
                    // (the listener will fire again on lastActive
                    // change, or we re-read the doc on next auth event).
                    const skeletonProfile: UserProfile = {
                      uid: user.uid,
                      emailVerified: user.emailVerified,
                      userType: user.isAnonymous ? "anonymous" : "registered",
                      createdAt: new Date(),
                      lastActive: new Date(),
                      preferences: DEFAULT_PREFERENCES,
                      gdprConsent: DEFAULT_GDPR_CONSENT,
                    };
                    if (user.email && user.email.trim()) {
                      skeletonProfile.email = user.email;
                    }
                    if (user.displayName && user.displayName.trim()) {
                      skeletonProfile.displayName = user.displayName;
                    }

                    set({
                      user,
                      profile: skeletonProfile,
                      isAuthenticated: true,
                      authMode: user.isAnonymous ? "anonymous" : "registered",
                      isLoading: false,
                      error: null,
                    });

                    if (!hasResolved) {
                      hasResolved = true;
                      resolve();
                    }
                    return;
                  }

                  // Create profile for new user
                  console.log("Creating new user profile. User object:", {
                    uid: user.uid,
                    email: user.email,
                    displayName: user.displayName,
                    emailVerified: user.emailVerified,
                    isAnonymous: user.isAnonymous,
                  });

                  profile = {
                    uid: user.uid,
                    emailVerified: user.emailVerified,
                    userType: user.isAnonymous ? "anonymous" : "registered",
                    createdAt: new Date(),
                    lastActive: new Date(),
                    preferences: DEFAULT_PREFERENCES,
                    gdprConsent: DEFAULT_GDPR_CONSENT,
                  };

                  // Only add email and displayName if they have actual string values
                  if (
                    user.email &&
                    typeof user.email === "string" &&
                    user.email.trim()
                  ) {
                    profile.email = user.email;
                    console.log("Adding email to profile:", user.email);
                  }

                  if (
                    user.displayName &&
                    typeof user.displayName === "string" &&
                    user.displayName.trim()
                  ) {
                    profile.displayName = user.displayName;
                    console.log(
                      "Adding displayName to profile:",
                      user.displayName
                    );
                  }

                  console.log("Final profile before cleaning:", profile);
                  const cleanedProfile = cleanFirestoreData(profile);
                  console.log("Cleaned profile for Firestore:", cleanedProfile);

                  // Save new profile to Firestore
                  await setDoc(doc(db, "users", user.uid), cleanedProfile);
                  console.log("Profile saved successfully");
                }

                set({
                  user,
                  profile,
                  isAuthenticated: true,
                  authMode: user.isAnonymous ? "anonymous" : "registered",
                  isLoading: false,
                  error: null,
                });

                console.log(
                  "Auth state updated successfully for",
                  user.isAnonymous ? "anonymous" : "registered",
                  "user"
                );

                // ── Live profile listener ─────────────────────────
                // Subscribe to /users/{uid} so server-side changes
                // (curator writes, admin overrides like
                // `allowDesktop`, multi-tab GDPR consent
                // updates) propagate without a reload. Previously
                // the profile was a one-shot getDoc that went
                // stale — flipping `allowDesktop` in the
                // Firestore console required the user to log out
                // and back in. Now it just works.
                //
                // Cleaned up when:
                //   - The auth listener fires for a different uid
                //     (sign-out → sign-in, account switch)
                //   - initializeAuth is called again
                //     (StrictMode dev double-invoke, hot reload)
                if (
                  listenedProfileUid !== user.uid ||
                  currentProfileUnsub == null
                ) {
                  if (currentProfileUnsub) {
                    try {
                      currentProfileUnsub();
                    } catch {
                      /* ignore */
                    }
                    currentProfileUnsub = null;
                  }
                  const profileRef = doc(db, "users", user.uid);
                  currentProfileUnsub = onSnapshot(
                    profileRef,
                    (snap) => {
                      // Snapshot-driven update. We don't try to
                      // re-derive the full profile from scratch
                      // here (the create-path logic is in the
                      // outer effect) — we just merge in the
                      // mutable fields that the curator / admin
                      // / settings UI can change. This keeps the
                      // listener focused and avoids feedback
                      // loops with the local set() above.
                      const data = snap.data();
                      if (!data) return;
                      const localProfile = get().profile;
                      if (!localProfile) return;
                      const merged: UserProfile = {
                        ...localProfile,
                        // Live-tracked fields:
                        gdprConsent: data.gdprConsent
                          ? {
                              ...DEFAULT_GDPR_CONSENT,
                              ...data.gdprConsent,
                              consentDate:
                                data.gdprConsent.consentDate instanceof
                                Timestamp
                                  ? data.gdprConsent.consentDate.toDate()
                                  : localProfile.gdprConsent.consentDate,
                              ageVerificationDate:
                                data.gdprConsent.ageVerificationDate instanceof
                                Timestamp
                                  ? data.gdprConsent.ageVerificationDate.toDate()
                                  : localProfile.gdprConsent
                                      .ageVerificationDate,
                            }
                          : localProfile.gdprConsent,
                        preferences:
                          data.preferences || localProfile.preferences,
                        travelProfile: data.travelProfile
                          ? {
                              ...DEFAULT_USER_PROFILE,
                              ...data.travelProfile,
                              lastUpdated:
                                data.travelProfile.lastUpdated instanceof
                                Timestamp
                                  ? data.travelProfile.lastUpdated.toDate()
                                  : localProfile.travelProfile?.lastUpdated ||
                                    new Date(),
                              completedAt:
                                data.travelProfile.completedAt instanceof
                                Timestamp
                                  ? data.travelProfile.completedAt.toDate()
                                  : localProfile.travelProfile?.completedAt ||
                                    null,
                            }
                          : localProfile.travelProfile,
                        allowDesktop:
                          data.allowDesktop === true ? true : undefined,
                        emailVerified:
                          typeof data.emailVerified === "boolean"
                            ? data.emailVerified
                            : localProfile.emailVerified,
                      };
                      set({ profile: merged });
                    },
                    (err) => {
                      // Don't surface — the user is already
                      // authenticated and has a working profile;
                      // a transient listener error just means
                      // they won't see admin overrides until next
                      // session. Log for debugging.
                      console.warn(
                        "Profile listener error (non-fatal):",
                        err
                      );
                    }
                  );
                  listenedProfileUid = user.uid;
                }
              } else {
                // No user - try anonymous sign in only if this is the first time (no existing auth state)
                console.log("No authenticated user found");

                // Tear down any live profile listener for the
                // user that just signed out.
                if (currentProfileUnsub) {
                  try {
                    currentProfileUnsub();
                  } catch {
                    /* ignore */
                  }
                  currentProfileUnsub = null;
                  listenedProfileUid = null;
                }

                // Check if we should sign in anonymously
                const shouldSignInAnonymously = !hasResolved; // Only on initial load

                if (shouldSignInAnonymously) {
                  console.log(
                    "Attempting anonymous sign-in for new session..."
                  );
                  try {
                    await signInAnonymously(auth);
                    console.log(
                      "Anonymous sign-in successful, auth state will update via listener"
                    );
                    // Don't resolve here - let the auth state change handler do it
                    return;
                  } catch (error: any) {
                    console.error("Anonymous sign-in failed:", error);
                    set({
                      error: `Authentication failed: ${error.message}`,
                      isLoading: false,
                      user: null,
                      profile: null,
                      isAuthenticated: false,
                      authMode: "anonymous",
                    });
                  }
                } else {
                  // User was signed out
                  set({
                    user: null,
                    profile: null,
                    isAuthenticated: false,
                    authMode: "anonymous",
                    isLoading: false,
                    error: null,
                  });

                  console.log("User signed out, auth state cleared");
                }
              }

              // Resolve on first successful completion
              if (!hasResolved) {
                hasResolved = true;
                resolve();
              }
            } catch (error) {
              console.error("Error in auth state change handler:", error);
              set({
                error: "Failed to load user profile",
                isLoading: false,
              });

              // Still resolve to prevent infinite loading
              if (!hasResolved) {
                hasResolved = true;
                resolve();
              }
            }
          });

          // Capture so future initializeAuth calls (StrictMode
          // double-invoke, hot reload, defensive re-init) can
          // tear this down. The previous version `return`ed
          // unsubscribe from inside a Promise executor — it went
          // nowhere, and the listener stayed alive forever.
          currentAuthUnsub = unsubscribe;
        });
      },

      // Sign in with email/password - now handles anonymous users properly
      signInWithEmail: async (email: string, password: string) => {
        try {
          set({ isLoading: true, error: null });

          // If user is currently anonymous, sign them out first and
          // then sign in normally. This is a destructive operation —
          // anonymous data on the device is lost. The CALLER is
          // expected to have warned the user (see authStore action
          // `confirmDestructiveSignIn` flag pattern in the form).
          const currentUser = auth.currentUser;
          if (currentUser && currentUser.isAnonymous) {
            console.log(
              "Anonymous user signing in to existing account — anonymous session will be discarded"
            );
            await firebaseSignOut(auth);
          }

          console.log("Signing in with email/password...");
          await signInWithEmailAndPassword(auth, email, password);
          console.log("Sign in successful");

          // Profile will be loaded by auth state listener
        } catch (error: any) {
          console.error("Sign in failed:", error);
          const friendly = friendlyAuthError(error);
          if (friendly === null) {
            // Treated as non-error (e.g. popup cancel — n/a here but
            // defensive). Don't surface error UI.
            set({ isLoading: false });
            return;
          }
          set({
            error: friendly,
            isLoading: false,
          });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      // Sign up with email/password with GDPR consent
      signUpWithEmail: async (
        email: string,
        password: string,
        displayName?: string,
        gdprConsent?: Partial<GDPRConsent>
      ) => {
        beginSignup();
        try {
          set({ isLoading: true, error: null });

          // Defensive guard: anonymous users should always go through
          // upgradeAnonymousAccount (linkWithCredential) so they keep
          // their UID and all associated data. If we reach here with
          // an anonymous user, the caller has a bug — we throw rather
          // than silently destroying their data.
          const currentUser = auth.currentUser;
          if (currentUser && currentUser.isAnonymous) {
            throw new Error(
              "Refusing to sign up an anonymous user via signUpWithEmail (would destroy data). Use upgradeAnonymousAccount instead."
            );
          }

          const result = await createUserWithEmailAndPassword(
            auth,
            email,
            password
          );

          if (displayName) {
            await updateProfile(result.user, { displayName });
          }

          await sendEmailVerification(result.user);

          // Create user profile with GDPR consent
          const profile: UserProfile = {
            uid: result.user.uid,
            emailVerified: false,
            userType: "registered",
            createdAt: new Date(),
            lastActive: new Date(),
            preferences: DEFAULT_PREFERENCES,
            gdprConsent: {
              ...DEFAULT_GDPR_CONSENT,
              ...gdprConsent,
              consentDate: new Date(),
            },
          };

          // Add optional fields
          if (email && email.trim()) {
            profile.email = email;
          }
          if (displayName && displayName.trim()) {
            profile.displayName = displayName;
          }

          const cleanedProfile = cleanFirestoreData(profile);
          await setDoc(doc(db, "users", result.user.uid), cleanedProfile, {
            merge: true,
          });

          set({
            user: result.user,
            profile,
            isAuthenticated: true,
            authMode: "registered",
            isLoading: false,
            error: null,
          });
        } catch (error) {
          const friendly = friendlyAuthError(error);
          if (friendly === null) {
            set({ isLoading: false });
            return;
          }
          set({
            error: friendly,
            isLoading: false,
          });
          throw error;
        } finally {
          endSignup();
          set({ isLoading: false });
        }
      },

      // Upgrade anonymous account using linkWithCredential with GDPR consent
      upgradeAnonymousAccount: async (
        email: string,
        password: string,
        displayName?: string,
        gdprConsent?: Partial<GDPRConsent>
      ) => {
        beginSignup();
        try {
          const currentUser = auth.currentUser;
          if (!currentUser || !currentUser.isAnonymous) {
            throw new Error("No anonymous user to upgrade");
          }

          set({ isLoading: true, error: null });

          // Create credential and link it
          const credential = EmailAuthProvider.credential(email, password);
          const result = await linkWithCredential(currentUser, credential);

          if (displayName) {
            await updateProfile(result.user, { displayName });
          }

          await sendEmailVerification(result.user);

          // Build the upgraded profile. Always create a complete
          // profile object — relying on `get().profile` being populated
          // is risky during init.
          const existingProfile = get().profile;
          const upgradedProfile: UserProfile = {
            uid: result.user.uid,
            emailVerified: result.user.emailVerified,
            userType: "registered",
            createdAt: existingProfile?.createdAt || new Date(),
            lastActive: new Date(),
            preferences: existingProfile?.preferences || DEFAULT_PREFERENCES,
            gdprConsent: {
              ...DEFAULT_GDPR_CONSENT,
              ...(existingProfile?.gdprConsent || {}),
              ...gdprConsent,
              consentDate: new Date(),
            },
          };

          if (email && email.trim()) {
            upgradedProfile.email = email;
          }
          if (displayName && displayName.trim()) {
            upgradedProfile.displayName = displayName;
          } else if (existingProfile?.displayName) {
            upgradedProfile.displayName = existingProfile.displayName;
          }
          if (existingProfile?.travelProfile) {
            upgradedProfile.travelProfile = existingProfile.travelProfile;
          }
          // Carry the desktop-gate override across the upgrade so a
          // staff member who's been granted desktop access doesn't
          // get locked out of it when they convert anonymous → registered.
          if (existingProfile?.allowDesktop === true) {
            upgradedProfile.allowDesktop = true;
          }

          await setDoc(
            doc(db, "users", result.user.uid),
            cleanFirestoreData(upgradedProfile),
            { merge: true }
          );

          set({
            user: result.user,
            profile: upgradedProfile,
            isAuthenticated: true,
            authMode: "registered",
            isLoading: false,
            error: null,
          });

          console.log(
            "Successfully upgraded anonymous account to registered, UID preserved:",
            result.user.uid
          );
        } catch (error) {
          const friendly = friendlyAuthError(error);
          if (friendly === null) {
            set({ isLoading: false });
            return;
          }
          set({
            error: friendly,
            isLoading: false,
          });
          throw error;
        } finally {
          endSignup();
        }
      },

      // Sign in / sign up with Google while applying GDPR consent
      signInWithGoogle: async (
        gdprConsent?: Partial<GDPRConsent>,
        displayName?: string
      ) => {
        beginSignup();
        try {
          set({ isLoading: true, error: null });

          const provider = new GoogleAuthProvider();
          provider.setCustomParameters({ prompt: "select_account" });

          const currentUser = auth.currentUser;
          const isAnonymous = currentUser?.isAnonymous;

          console.log(
            "[Google sign-in] starting",
            {
              currentUid: currentUser?.uid,
              isAnonymous,
              flow: isAnonymous ? "linkWithPopup" : "signInWithPopup",
            }
          );

          // Try to link the anonymous user with Google. If the Google
          // account is already attached to an existing Instant Tours
          // user, Firebase throws auth/credential-already-in-use — we
          // can't merge the two accounts, but we can sign the user
          // INTO the existing account so they're at least not stuck.
          // Anonymous data on the device is lost in this case, which
          // is the standard Firebase behavior.
          let result;
          try {
            result = isAnonymous
              ? await linkWithPopup(currentUser!, provider)
              : await signInWithPopup(auth, provider);
            console.log("[Google sign-in] popup resolved", {
              uid: result?.user?.uid,
              email: result?.user?.email,
              providerIds: result?.user?.providerData?.map((p) => p.providerId),
              isAnonymousNow: result?.user?.isAnonymous,
            });
          } catch (linkErr: any) {
            const code = linkErr?.code || "";
            console.log("[Google sign-in] popup error", {
              code,
              message: linkErr?.message,
              email:
                linkErr?.customData?.email ||
                linkErr?.email ||
                null,
              hasCredential:
                !!GoogleAuthProvider.credentialFromError(linkErr),
            });

            // Popup dismissed / blocked: not a real error. Surface a
            // gentle message and bail without throwing — the form
            // will see error state and re-enable the button.
            if (
              code === "auth/popup-closed-by-user" ||
              code === "auth/cancelled-popup-request" ||
              code === "auth/user-cancelled"
            ) {
              set({
                error: null,
                isLoading: false,
              });
              return;
            }
            if (code === "auth/popup-blocked") {
              set({
                error:
                  "Your browser blocked the Google sign-in popup. Allow popups for this site and try again.",
                isLoading: false,
              });
              throw linkErr;
            }

            // The Google account / email is already in use by an
            // existing Firebase user under a DIFFERENT provider
            // (typically email/password). Rather than telling the
            // user to figure it out themselves, we capture the
            // attempted Google credential and surface a "sign in
            // to link" step in the auth modal. After they enter
            // their existing password, completeGoogleLink signs
            // them in and links the Google credential automatically
            // — so this becomes the LAST time they ever have to
            // enter their password.
            const linkableCodes = new Set([
              "auth/account-exists-with-different-credential",
              // When NOT anonymous, "credential-already-in-use" can
              // also fire if the Google user exists with a different
              // uid (Firebase project setting "Multiple accounts per
              // email" — uncommon but possible). Same remedy.
              "auth/credential-already-in-use",
              "auth/email-already-in-use",
            ]);

            if (linkableCodes.has(code)) {
              const credential =
                GoogleAuthProvider.credentialFromError(linkErr);
              const linkEmail =
                linkErr?.customData?.email ||
                linkErr?.email ||
                null;

              console.log("[Google sign-in] reconcile path triggered", {
                code,
                hasCredential: !!credential,
                linkEmail,
              });

              if (credential && linkEmail) {
                // Discover what providers the existing account has.
                // Three cases matter to us:
                //
                //   1. Existing account has google.com → the user
                //      just authenticated with the same Google
                //      account that's already attached to a
                //      different Firebase user. We don't need a
                //      password — just signInWithCredential and
                //      they're in. This is the "I signed up with
                //      Google originally and never set a password"
                //      case. Anonymous data is lost (unavoidable;
                //      we can't merge two real Firebase users
                //      client-side).
                //
                //   2. Existing account has password → user
                //      originally signed up with email+password
                //      and is now adding Google. We collect their
                //      password, sign them in, and link Google as
                //      a second provider so future Google logins
                //      work without a prompt.
                //
                //   3. Some other provider (Apple, Facebook, …) →
                //      we don't currently support that link path.
                //      Fall through to a clear error.
                let methods: string[] = [];
                try {
                  methods = await fetchSignInMethodsForEmail(
                    auth,
                    linkEmail
                  );
                  console.log(
                    "[Google sign-in] existing providers for",
                    linkEmail,
                    methods
                  );
                } catch (mErr) {
                  // Don't fail the whole flow if this lookup
                  // errors — fall back to the password prompt,
                  // which can also surface its own friendlier
                  // error if the user has no password.
                  console.warn(
                    "[Google sign-in] fetchSignInMethodsForEmail failed:",
                    mErr
                  );
                }

                if (methods.includes("google.com")) {
                  // Case 1: direct sign-in, no password.
                  console.log(
                    "[Google sign-in] existing Google account — direct signIn"
                  );
                  try {
                    const r = await signInWithCredential(
                      auth,
                      credential
                    );
                    console.log(
                      "[Google sign-in] signInWithCredential ok",
                      { uid: r.user.uid, email: r.user.email }
                    );
                    // The auth listener will pick up the new user
                    // and load/create their profile. Clear store
                    // flags so the modal can close cleanly.
                    set({
                      pendingGoogleLink: null,
                      authModal: {
                        ...get().authModal,
                        open: false,
                      },
                      isLoading: false,
                      error: null,
                    });
                    return;
                  } catch (sErr: any) {
                    console.warn(
                      "[Google sign-in] signInWithCredential failed:",
                      sErr
                    );
                    set({
                      error:
                        sErr?.message ||
                        "Couldn't sign in with Google. Try again.",
                      isLoading: false,
                    });
                    throw sErr;
                  }
                }

                // Empty methods means we can't tell what provider
                // the existing account uses. Firebase's "Email
                // enumeration protection" setting (on by default
                // for newer projects) deliberately hides this. The
                // captured Google credential is still valid though,
                // so OPTIMISTICALLY try a direct Google sign-in
                // first — if the existing account is in fact a
                // Google account (the most common case for users
                // hitting credential-already-in-use), this Just
                // Works with no password prompt. Only fall back
                // to the password step if Firebase rejects the
                // direct credential sign-in.
                if (methods.length === 0) {
                  console.log(
                    "[Google sign-in] no provider info (likely email enumeration protection on); attempting direct signIn"
                  );
                  try {
                    const r = await signInWithCredential(
                      auth,
                      credential
                    );
                    console.log(
                      "[Google sign-in] direct signInWithCredential ok",
                      { uid: r.user.uid, email: r.user.email }
                    );
                    set({
                      pendingGoogleLink: null,
                      authModal: {
                        ...get().authModal,
                        open: false,
                      },
                      isLoading: false,
                      error: null,
                    });
                    return;
                  } catch (sErr: any) {
                    // Direct sign-in failed. The existing account
                    // is presumably email+password. Fall through
                    // to the password collection step.
                    console.log(
                      "[Google sign-in] direct signIn failed, falling back to password prompt:",
                      sErr?.code
                    );
                    set({
                      pendingGoogleLink: {
                        email: linkEmail,
                        credential,
                        displayName: null,
                      },
                      error: null,
                      isLoading: false,
                    });
                    return;
                  }
                }

                if (methods.includes("password")) {
                  // Case 2: existing email+password account. Park
                  // the credential so the modal can render the
                  // password-collection step.
                  set({
                    pendingGoogleLink: {
                      email: linkEmail,
                      credential,
                      displayName: null,
                    },
                    error: null,
                    isLoading: false,
                  });
                  return;
                }

                // Case 3: existing account uses a provider we
                // don't auto-link from. Surface a clear message.
                set({
                  error: `This email is already registered using ${methods[0]}. Sign in with that provider instead.`,
                  isLoading: false,
                });
                throw linkErr;
              }

              // Couldn't extract the credential or email — fall
              // back to the original explanatory error so the user
              // at least knows what's happening.
              set({
                error:
                  "This email is already registered with a different sign-in method. Sign in with your password.",
                isLoading: false,
              });
              throw linkErr;
            } else if (code === "auth/network-request-failed") {
              set({
                error:
                  "Network error during Google sign-in. Check your connection and try again.",
                isLoading: false,
              });
              throw linkErr;
            } else if (code === "auth/unauthorized-domain") {
              set({
                error:
                  "This domain isn't authorized for Google sign-in. Contact support.",
                isLoading: false,
              });
              throw linkErr;
            } else {
              // Unknown error — surface raw message so support has
              // something to work with.
              set({
                error:
                  linkErr?.message ||
                  "Couldn't sign in with Google. Try again.",
                isLoading: false,
              });
              throw linkErr;
            }
          }

          if (!result) {
            // Defensive: if a code path above set state and returned
            // without populating result, exit cleanly.
            return;
          }

          const user = result.user;
          const profileRef = doc(db, "users", user.uid);
          const existingProfile = await getDoc(profileRef);
          const existingData = existingProfile.exists()
            ? existingProfile.data()
            : null;

          // Returning user: profile exists with valid GDPR consent
          // already recorded. Sign them in directly — they've
          // already agreed to the terms in a previous session.
          const hasPriorConsent =
            !!existingData?.gdprConsent?.consentDate &&
            !!existingData?.gdprConsent?.privacyPolicyVersion;

          // First-call path explicitly passes a gdprConsent (the
          // legacy flow with a pre-filled form). Honor that — it
          // means the caller has already collected consent and we
          // can write the profile directly.
          const callerProvidedConsent =
            !!gdprConsent && !!gdprConsent.consentDate;

          if (!hasPriorConsent && !callerProvidedConsent) {
            // NEW user, no consent on file. Defer the profile write
            // and surface a "needs consent" state. The auth modal
            // will render the consent step. We do NOT set
            // isAuthenticated: true yet — the user hasn't agreed
            // to terms, and we don't want the rest of the app
            // treating them as a real user until they have.
            console.log(
              "Google sign-in: new user, awaiting GDPR consent"
            );
            set({
              pendingGoogleConsent: {
                uid: user.uid,
                email: user.email || null,
                displayName: user.displayName || null,
              },
              isLoading: false,
              error: null,
              // Note: we DON'T set isAuthenticated. The Firebase
              // auth user exists, but the app gate stays closed.
            });
            return;
          }

          // Returning user OR caller-provided consent path.
          // Merge any existing GDPR consent (returning user) with
          // what the caller passed in (re-consent or fresh signup).
          const mergedConsent: GDPRConsent = {
            ...DEFAULT_GDPR_CONSENT,
            ...(existingData?.gdprConsent || {}),
            ...gdprConsent,
            consentDate: existingData?.gdprConsent?.consentDate
              ? existingData.gdprConsent.consentDate.toDate
                ? existingData.gdprConsent.consentDate.toDate()
                : new Date(existingData.gdprConsent.consentDate)
              : new Date(),
          };

          const resolvedDisplayName =
            displayName ||
            user.displayName ||
            existingData?.displayName ||
            "";

          // Build the full profile shape we'll write AND reflect in
          // store state. Whether this is a returning user or a fresh
          // signup, we end up with a complete UserProfile object so
          // the rest of the app doesn't see a mid-state.
          const profile: UserProfile = {
            uid: user.uid,
            emailVerified: user.emailVerified,
            userType: "registered",
            createdAt: existingData?.createdAt?.toDate
              ? existingData.createdAt.toDate()
              : existingData?.createdAt
              ? new Date(existingData.createdAt)
              : new Date(),
            lastActive: new Date(),
            preferences: existingData?.preferences || DEFAULT_PREFERENCES,
            gdprConsent: mergedConsent,
          };

          if (user.email && user.email.trim()) {
            profile.email = user.email;
          }
          if (resolvedDisplayName && resolvedDisplayName.trim()) {
            profile.displayName = resolvedDisplayName.trim();
          }
          // Preserve travel profile from prior data if present
          if (existingData?.travelProfile) {
            profile.travelProfile = {
              ...DEFAULT_USER_PROFILE,
              ...existingData.travelProfile,
              lastUpdated: existingData.travelProfile.lastUpdated?.toDate
                ? existingData.travelProfile.lastUpdated.toDate()
                : new Date(),
              completedAt: existingData.travelProfile.completedAt?.toDate
                ? existingData.travelProfile.completedAt.toDate()
                : null,
            };
          }
          // Preserve the desktop-gate override too — losing it on a
          // re-link would lock a staff/QA user out of desktop access
          // they'd previously been granted.
          if (existingData?.allowDesktop === true) {
            profile.allowDesktop = true;
          }

          // Single write path — `merge: true` works for both create and
          // update cases, so we don't need to branch.
          await setDoc(profileRef, cleanFirestoreData(profile), {
            merge: true,
          });

          set({
            user,
            profile,
            isAuthenticated: true,
            authMode: "registered",
            isLoading: false,
            error: null,
            pendingGoogleConsent: null,
          });
        } catch (error) {
          // Specific error states have already been set above. This
          // catch handles any leftover unexpected failures.
          const existingError = get().error;
          if (!existingError) {
            set({
              error: (error as Error).message,
              isLoading: false,
            });
          }
          throw error;
        } finally {
          endSignup();
        }
      },

      // Finalize a deferred Google sign-in by recording GDPR consent
      // and writing the user profile. Called when the user clicks
      // Accept on the consent step that follows a fresh Google
      // sign-in. The auth user already exists at this point — we're
      // just provisioning the Firestore profile.
      completeGoogleConsent: async (gdprConsent: Partial<GDPRConsent>) => {
        const pending = get().pendingGoogleConsent;
        if (!pending) {
          console.warn("completeGoogleConsent called with no pending state");
          return;
        }
        const user = auth.currentUser;
        if (!user || user.uid !== pending.uid) {
          // Auth state diverged — bail. The modal will show an
          // error and the user can try again.
          set({
            error: "Sign-in state mismatch. Try again.",
            pendingGoogleConsent: null,
            isLoading: false,
          });
          return;
        }
        try {
          set({ isLoading: true, error: null });

          const mergedConsent: GDPRConsent = {
            ...DEFAULT_GDPR_CONSENT,
            ...gdprConsent,
            consentDate: new Date(),
          };

          const profile: UserProfile = {
            uid: user.uid,
            emailVerified: user.emailVerified,
            userType: "registered",
            createdAt: new Date(),
            lastActive: new Date(),
            preferences: DEFAULT_PREFERENCES,
            gdprConsent: mergedConsent,
          };
          if (user.email && user.email.trim()) profile.email = user.email;
          if (user.displayName && user.displayName.trim()) {
            profile.displayName = user.displayName.trim();
          } else if (pending.displayName) {
            profile.displayName = pending.displayName;
          }

          await setDoc(
            doc(db, "users", user.uid),
            cleanFirestoreData(profile),
            { merge: true }
          );

          set({
            user,
            profile,
            isAuthenticated: true,
            authMode: "registered",
            isLoading: false,
            error: null,
            pendingGoogleConsent: null,
          });
        } catch (err) {
          console.error("completeGoogleConsent failed:", err);
          set({
            error:
              (err as Error).message ||
              "Couldn't save your consent. Try again.",
            isLoading: false,
          });
          throw err;
        }
      },

      // Cancel a pending Google sign-in. The user declined consent,
      // so we delete the Firebase auth user (they never agreed to
      // the terms — keeping the auth record would be a problem) and
      // sign them out. They end up back at the anonymous-or-no-user
      // state.
      cancelGoogleConsent: async () => {
        const pending = get().pendingGoogleConsent;
        if (!pending) return;
        const user = auth.currentUser;
        try {
          set({ isLoading: true });
          if (user && user.uid === pending.uid) {
            try {
              // Try to delete the Firebase user. If the session is
              // recent (which it is — the user just signed in) this
              // works without re-authentication.
              await deleteUser(user);
            } catch (err) {
              console.warn(
                "Couldn't delete Firebase user on cancel; signing out instead",
                err
              );
              try {
                await firebaseSignOut(auth);
              } catch {
                /* swallow */
              }
            }
          }
        } finally {
          // Clear local state regardless. initializeAuth's listener
          // will pick up the auth change and reset to anonymous.
          set({
            user: null,
            profile: null,
            isAuthenticated: false,
            authMode: "anonymous",
            pendingGoogleConsent: null,
            isLoading: false,
            error: null,
          });
        }
      },

      // Auto-reconcile path: user tried to sign in with Google, but
      // the email is already registered with email/password. The
      // signInWithGoogle handler captured the OAuth credential and
      // parked it as pendingGoogleLink. Now the user has provided
      // the password for the existing account; we sign them in and
      // attach the Google credential as a second provider on the
      // same Firebase user. Future Google sign-ins for this email
      // will succeed without going through this flow again.
      completeGoogleLink: async (password: string) => {
        const pending = get().pendingGoogleLink;
        if (!pending) {
          console.warn("completeGoogleLink called with no pending state");
          return;
        }
        try {
          set({ isLoading: true, error: null });

          // Sign in to the existing account using the password the
          // user just typed. This restores the user's original uid
          // and their existing Firestore profile (preferences,
          // travel profile, conversations subcollection, etc.).
          await signInWithEmailAndPassword(
            auth,
            pending.email,
            password
          );

          // Attach the Google credential to that user. From here on,
          // Google sign-in for this email maps to the same uid.
          const linkedUser = auth.currentUser;
          if (linkedUser) {
            try {
              await linkWithCredential(linkedUser, pending.credential);
            } catch (linkErr: any) {
              // The most common benign case is "provider-already-
              // -linked" (somehow already attached) — treat as
              // success. Other errors get logged but don't block
              // the sign-in itself, which has already succeeded.
              const code = linkErr?.code || "";
              if (code !== "auth/provider-already-linked") {
                console.warn(
                  "Google credential link failed (sign-in still succeeded):",
                  linkErr
                );
              }
            }
          }

          // Sign-in succeeded → clear pending state. The auth
          // listener has already updated user/profile/isAuthenticated
          // from the email sign-in.
          set({
            pendingGoogleLink: null,
            isLoading: false,
            error: null,
          });
        } catch (error: any) {
          // The most common failure here is wrong-password. Surface
          // a friendly message. We keep `pendingGoogleLink` set so
          // the user can retry without losing the captured Google
          // credential.
          const code = error?.code || "";
          let friendly = "Couldn't sign in. Try again.";
          if (
            code === "auth/wrong-password" ||
            code === "auth/invalid-credential" ||
            code === "auth/invalid-login-credentials"
          ) {
            friendly = "That password doesn't match the account.";
          } else if (code === "auth/user-not-found") {
            // Email doesn't actually have an email/password account.
            // The pending Google credential is now stranded; bail
            // and let the user start over.
            friendly =
              "No password account found for this email. Try signing in with Google again.";
            set({ pendingGoogleLink: null });
          } else if (code === "auth/too-many-requests") {
            friendly =
              "Too many attempts. Wait a minute and try again, or reset your password.";
          } else if (code === "auth/network-request-failed") {
            friendly =
              "Network error. Check your connection and try again.";
          } else if (error?.message) {
            friendly = error.message;
          }
          set({ error: friendly, isLoading: false });
          throw error;
        }
      },

      cancelGoogleLink: () => {
        // Drop the captured credential and return the user to the
        // tabbed login/signup view. No Firebase auth state to clean
        // up — we never actually signed anyone in for the link
        // flow; the popup closed before that happened.
        set({ pendingGoogleLink: null, error: null });
      },

      openAuthModal: (initialTab: "login" | "signup" = "login") => {
        // Reset per-modal state on open: clear stale errors so a
        // previous failed attempt doesn't bleed into this fresh
        // session.
        set({
          authModal: { open: true, initialTab },
          error: null,
        });
      },

      closeAuthModal: () => {
        // Clear all transient flow state on close. If a Google
        // link/consent flow was mid-flight we drop it the same way
        // a user-driven cancel would. The user can re-initiate
        // from scratch.
        const state = get();
        if (state.pendingGoogleLink) {
          set({ pendingGoogleLink: null });
        }
        // pendingGoogleConsent has its own cancel path that needs
        // to delete the half-created Firebase user, so we don't
        // touch it here — cancelGoogleConsent must be invoked
        // explicitly from the consent step.
        set({
          authModal: { ...state.authModal, open: false },
          error: null,
        });
      },

      // Update GDPR consent
      updateGDPRConsent: async (consent: Partial<GDPRConsent>) => {
        try {
          const user = auth.currentUser;
          const currentProfile = get().profile;

          if (!user || !currentProfile) {
            throw new Error("No user logged in");
          }

          const updatedConsent = {
            ...currentProfile.gdprConsent,
            ...consent,
          };

          const updatedProfile = {
            ...currentProfile,
            gdprConsent: updatedConsent,
            lastActive: new Date(),
          };

          const cleanProfile = cleanFirestoreData(updatedProfile);
          await setDoc(doc(db, "users", user.uid), cleanProfile);
          set({ profile: updatedProfile });
        } catch (error) {
          set({ error: (error as Error).message });
          throw error;
        }
      },

      // ── Explicit consent recorder ────────────────────────────────
      // Used by the cookie banner (accept/decline) and any future
      // privacy-settings sheet. Writes ONLY gdprConsent +
      // lastActive — friendly to the Firestore-rules update gate
      // and avoids the surface area of a full setDoc overwrite.
      //
      // Always flips `consent_recorded: true` and stamps
      // `consentDate: now`, regardless of whether the user
      // accepted or declined. Both are active choices; the
      // booleans inside record what they chose.
      recordGdprConsent: async (consent) => {
        try {
          const user = auth.currentUser;
          if (!user) {
            // No auth yet — log and bail. The banner shouldn't
            // call this before anonymous sign-in has resolved,
            // but if it does (race on first paint) we don't want
            // to throw and show an error toast.
            console.warn(
              "recordGdprConsent: no auth user; skipping (will retry once signed in)"
            );
            return;
          }

          const current = get().profile?.gdprConsent || DEFAULT_GDPR_CONSENT;
          const next: GDPRConsent = {
            ...current,
            // Necessary is implicitly true; the user can't refuse
            // strictly-necessary cookies (auth, session). The
            // banner doesn't expose a toggle for it.
            necessary: true,
            functional: consent.functional ?? current.functional,
            analytics: consent.analytics ?? current.analytics,
            marketing: consent.marketing ?? current.marketing,
            consentDate: new Date(),
            consent_recorded: true,
            privacyPolicyVersion: current.privacyPolicyVersion || "3.0",
          };

          // updateDoc + setDoc-merge fallback: updateDoc fails if
          // the doc doesn't exist (rare, but possible if the
          // listener race wrote nothing yet). setDoc-merge always
          // succeeds and matches the create-rule schema (which
          // requires gdprConsent at create time).
          const ref = doc(db, "users", user.uid);
          try {
            await updateDoc(ref, {
              gdprConsent: cleanFirestoreData(next),
              lastActive: new Date(),
            });
          } catch (err: any) {
            if (err?.code === "not-found") {
              await setDoc(
                ref,
                {
                  uid: user.uid,
                  userType: user.isAnonymous ? "anonymous" : "registered",
                  createdAt: new Date(),
                  lastActive: new Date(),
                  preferences: DEFAULT_PREFERENCES,
                  gdprConsent: cleanFirestoreData(next),
                },
                { merge: true }
              );
            } else {
              throw err;
            }
          }

          // Mirror locally so the banner dismisses immediately
          // (no need to wait for the profile listener round-trip).
          const localProfile = get().profile;
          if (localProfile) {
            set({
              profile: {
                ...localProfile,
                gdprConsent: next,
                lastActive: new Date(),
              },
            });
          }
        } catch (error) {
          set({ error: (error as Error).message });
          throw error;
        }
      },

      // Reset password
      resetPassword: async (email: string) => {
        try {
          set({ error: null });
          await sendPasswordResetEmail(auth, email);
        } catch (error) {
          set({ error: (error as Error).message });
          throw error;
        }
      },

      // Resend email verification
      resendVerification: async () => {
        try {
          const user = auth.currentUser;
          if (!user) throw new Error("No user logged in");
          if (user.emailVerified) throw new Error("Email already verified");

          await sendEmailVerification(user);
        } catch (error) {
          set({ error: (error as Error).message });
          throw error;
        }
      },

      // Sign out
      signOut: async () => {
        try {
          // Sign out of Firebase. The auth listener will fire with
          // `null`, clearing user/profile and flipping
          // isAuthenticated to false.
          await firebaseSignOut(auth);

          // Immediately re-establish an anonymous session so the
          // app's protected routes have something to resolve to.
          // Without this, every <ProtectedRoute> redirects to /chat,
          // which is itself protected → infinite redirect → blank
          // white screen with no nav. Anonymous mode is the same
          // "guest" state new visitors land in, so this is just
          // returning the user to the equivalent of a fresh visit.
          //
          // We don't await profile creation here — the auth listener
          // does that work when it fires for the new anonymous user.
          // We only need the auth side of things to be in a stable
          // state before this function returns.
          try {
            await signInAnonymously(auth);
          } catch (anonErr) {
            // If anonymous sign-in fails (e.g. provider disabled in
            // Firebase Console, network blip), don't propagate —
            // signOut itself succeeded. The user can retry by
            // refreshing. Log so this is visible in support.
            console.warn(
              "Anonymous re-sign-in after signOut failed:",
              anonErr
            );
          }
        } catch (error) {
          set({ error: (error as Error).message });
          throw error;
        }
      },

      // Delete account
      deleteAccount: async () => {
        try {
          const user = auth.currentUser;
          if (!user) throw new Error("No user logged in");

          // Delete user profile from Firestore
          await deleteDoc(doc(db, "users", user.uid));

          // Delete authentication account
          await deleteUser(user);
        } catch (error) {
          set({ error: (error as Error).message });
          throw error;
        }
      },

      // Update user profile
      updateUserProfile: async (updates: Partial<UserProfile>) => {
        try {
          const user = auth.currentUser;
          const currentProfile = get().profile;

          if (!user || !currentProfile) {
            throw new Error("No user logged in");
          }

          // Only write what actually changed. With merge:true, Firestore
          // preserves all unspecified fields. Spreading currentProfile
          // here would round-trip Dates through serialization, flagging
          // unchanged fields as "affected" and breaking the rules check.
          const writePayload = cleanFirestoreData({
            ...updates,
            lastActive: new Date(),
          });
          const updatedProfile = {
            ...currentProfile,
            ...updates,
            lastActive: new Date(),
          };
          const cleanProfile = writePayload;

          // `merge: true` is critical here. Firestore security rules
          // gate updates on `request.resource.data.diff(resource.data)
          // .affectedKeys().hasOnly([...allowed])`. A full setDoc
          // replace can flip extra fields into affectedKeys (e.g.
          // Date↔Timestamp round-trip drift, fields that
          // cleanFirestoreData stripped because they round-tripped to
          // null/undefined, sub-fields that look unchanged in JS but
          // serialize differently). With merge, only fields actually
          // present in cleanProfile are written, so affectedKeys
          // reflects only real changes — and we match the same
          // pattern used by signUpWithEmail and completeGoogleConsent.
          await setDoc(doc(db, "users", user.uid), cleanProfile, {
            merge: true,
          });
          set({ profile: updatedProfile });
        } catch (error) {
          set({ error: (error as Error).message });
          throw error;
        }
      },

      // Clear error
      clearError: () => set({ error: null }),
    }),
    {
      name: "auth-storage",
      partialize: (state) => ({
        authMode: state.authMode,
      }),
    }
  )
);
